<template>
  <div
    class="input-range"
    :style="{
      '--range-limit': rangeLimit,
      '--range-value': rangeValue,
      '--bar-color': valueColor,
      '--decrease-svg': decreaseSVG,
      '--increase-svg': increaseSVG
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @pointerdown.stop=""
  >
    <slot name="before" :value="cachedValue"></slot>

    <div class="input-range__bar">
      <div
        v-if="highlightChanges"
        class="input-range__oldvalue-marker"
        :style="{
          left: `calc(${oldRangeValue}% + ${valueOffset(oldRangeValue)})`
        }"
      ></div>
      <input
        ref="inputElement"
        class="input-range__range"
        type="range"
        :list="calculatedDatalistOptions ? `${uniqueId}-list` : undefined"
        :value="cachedValue"
        :min="min"
        :max="max"
        :disabled="disabled"
        :step="step"
        @focus="onMouseEnter"
        @blur="onMouseLeave"
        @change="onChange"
        @input="onComponentInput($event)"
      />
    </div>

    <datalist :id="`${uniqueId}-list`" v-if="calculatedDatalistOptions">
      <option v-for="option in datalistOptions" :value="option"></option>
    </datalist>

    <floating-element
      v-if="tooltipEnabled"
      :visible="mouseInside"
      class="input-range__tooltip"
      :x="tooltipPosition.x"
      :y="tooltipPosition.y"
      y-alignment="bottom"
      x-alignment="center"
      transition-name="transition-fade"
      :style="{ marginLeft: thumbOffset }"
    >
      <slot name="tooltip" :value="cachedValue">
        {{ formatNumber(cachedValue) }}
      </slot>
    </floating-element>

    <slot
      name="after"
      :value="cachedValue"
      :min-value="min"
      :max-value="maxValue"
      :disabled="disabled"
      :on-update="updateValue"
      :on-change="onChange"
    ></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect, ref } from "vue";
import { useMagicKeys } from "@vueuse/core";
import { v4 } from "uuid";
import { formatNumber } from "@/helpers/utility/Formatting";
import FloatingElement from "@core/ui/FloatingElement.vue";

interface Props {
  value: number;
  oldValue?: number;
  id?: string;
  max?: number;
  min?: number;
  limit?: number | null;
  disabled?: boolean;
  colors?: string[];
  highlightChanges?: boolean;
  step?: number | "any";
  tooltipEnabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: "",
  max: 100,
  min: 0,
  limit: null,
  disabled: false,
  highlightChanges: false,
  step: 1,
  tooltipEnabled: true
});

const emit = defineEmits<{
  (e: "change", value: number): void;
  (e: "input", value: number): void;
  (e: "update:value", value: number): void;
}>();

const inputElement = ref<HTMLInputElement>();

const { shift } = useMagicKeys();

let cachedValue = ref(0);
const uniqueId = ref(props.id || v4());

const maxValue = computed(() => {
  if (!props.limit) return props.max;
  if (props.limit >= props.max) return props.max;
  return props.limit;
});
const valueRange = computed(() => {
  return props.max - props.min;
});
const rangeLimit = computed(() => {
  return Math.round(
    ((maxValue.value - props.min) / valueRange.value) * 100
  );
});
/**
 * The value of the range, mapped to a percentage (between 0 and 100).
 */
const rangeValue = computed(() => {
  if (valueRange.value === 0) return 100;
  return Math.round(
    ((cachedValue.value - props.min) / valueRange.value) * 100
  );
});
const oldRangeValue = computed(() => {
  if (props.oldValue == null) return rangeValue.value;
  if (valueRange.value === 0) return 100;
  return Math.round(
    ((props.oldValue || 0 - props.min) / valueRange.value) * 100
  );
});
const rangeIncrease = computed(() => {
  return Math.max(0, rangeValue.value - oldRangeValue.value);
});
const rangeDecrease = computed(() => {
  return Math.max(0, oldRangeValue.value - rangeValue.value);
});
const decreaseSVG = computed(() => {
  if (!props.highlightChanges)
    return "linear-gradient(to right, transparent, transparent)";
  // generate an SVG image with an pattern of arrows pointing to the left.
  // The width should be according to the rangeDecrease value.
  return svgToDataUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" >
    <defs>
      <pattern id="decrease-pattern" patternUnits="userSpaceOnUse" width="10" height="11">
        <rect x="0" y="0" width="10" height="11" fill="#000000a0" />
        <path d="M 8 0 L 3 6 L 8 12" fill="none" stroke="#ffffff50" stroke-width="3" />
      </pattern>
    </defs>
    <rect x="${rangeValue.value}%" y="0" width="${rangeDecrease.value}%" height="100%" fill="url(#decrease-pattern)" />
    <rect x="${oldRangeValue.value}%" y="0" width="1px" height="100%" fill="#00000055" />
  </svg>`);
});
const increaseSVG = computed(() => {
  if (!props.highlightChanges)
    return "linear-gradient(to right, transparent, transparent)";
  // generate an SVG image with an pattern of arrows pointing to the right.
  // The width should be according to the rangeIncrease value.
  return svgToDataUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" >
    <defs>
      <pattern id="increase-pattern" patternUnits="userSpaceOnUse" width="10" height="11">
        <rect x="0" y="0" width="10" height="11" fill="#00000060" />
        <path d="M 2 0 L 7 6 L 2 12" fill="none" stroke="#ffffff70" stroke-width="3" />
      </pattern>
    </defs>
    <rect x="${oldRangeValue.value}%" y="0" width="${rangeIncrease.value}%" height="100%" fill="url(#increase-pattern)" />
  </svg>`);
});

const valueColor = computed(() => {
  if (!props.colors) return undefined;
  if (props.colors.length == 0) return undefined;
  if (props.colors.length == 1) return props.colors[0];

  const percentValue = rangeValue.value / 100;

  const colorIndex = Math.max(
    Math.ceil(percentValue * props.colors.length) - 1,
    0
  );
  return props.colors[colorIndex];
});

const calculatedDatalistOptions = computed(() => {
  if (!shift.value || props.step === "any") {
    return undefined;
  }
  if (!mouseInside.value) {
    return undefined;
  }
  return datalistOptions.value;
});

const datalistOptions = computed(() => {
  // include only options that are multiples of 10
  const options = [];
  if (isNaN(maxValue.value)) return undefined;
  if (maxValue.value === Infinity) return undefined;
  const range = maxValue.value - props.min;
  let step = 1;
  while (step <= range / 100) {
    step *= 10;
  }
  const minRoundOption = Math.ceil(props.min / step) * step;
  for (let i = minRoundOption; i <= maxValue.value; i += step) {
    options.push(i);
  }
  if (options.length === 0) {
    return undefined;
  }
  if (options[0] !== props.min) {
    options.unshift(props.min);
  }
  if (options[options.length - 1] !== maxValue.value) {
    options.push(maxValue.value);
  }
  return options;
});

const tooltipPosition = computed((): { x: number; y: number } => {
  if (!inputElement.value) {
    return {
      x: 0,
      y: 0
    };
  }
  if (!mouseInside.value) {
    return {
      x: 0,
      y: 0
    };
  }
  const rect = inputElement.value.getBoundingClientRect();
  const x = rect.x + rect.width * rangeValue.value * 0.01;
  const y = rect.y - 5;
  return { x, y };
});

const thumbOffset = computed(() => {
  return valueOffset(rangeValue.value);
});

function valueOffset(value: number) {
  return `${(-value + 50) / 100}em`;
}

watchEffect(() => {
  updateCachedValue(props.value);
});

function onChange() {
  emit("update:value", cachedValue.value);
  emit("change", cachedValue.value);
  updateCachedValue(props.value);
}

function onComponentInput(inputEvent: Event) {
  const target = inputEvent.target as HTMLInputElement;
  let value = parseFloat(target.value);
  if (value > maxValue.value) {
    value = maxValue.value;
  }
  if (value < props.min) {
    value = props.min;
  }
  updateValue(value);
  emit("input", value);
}

function updateValue(value: number) {
  updateCachedValue(value);
  emit("update:value", value);
}

function updateCachedValue(value: number) {
  cachedValue.value = value;
  if (inputElement.value) {
    inputElement.value.value = `${value || 0}`;
  }
}

const mouseInside = ref(false);

function onMouseEnter() {
  mouseInside.value = true;
}

function onMouseLeave() {
  mouseInside.value = false;
}

function svgToDataUrl(svgString: string) {
  const encoded = encodeURIComponent(svgString)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22");
  return `url('data:image/svg+xml,${encoded}')`;
}
</script>

<style lang="scss">
.input-range {
  width: 100%;
  display: flex;
  align-items: center;
  // grid-template-columns: max-content minmax(0, 1fr) max-content;
  gap: 0.5em;
  position: relative;
  background-color: transparent;

  --bar-color: var(--color-action);

  --thumb-offset: calc(-1em * (var(--range-value) - 50) / 100);
  --thumb-center: calc(var(--range-value) * 1% + var(--thumb-offset));

  &__bar {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  &__range {
    position: relative;
    min-width: 0;
    width: 100%;
    height: 1em;
    align-items: center;
    appearance: none;
    background-color: transparent;
    z-index: 1;

    @mixin input-range-track {
      background-color: var(--color-background-medium);
      border-radius: 500rem / 400rem;
      border: none;
      height: 0.8em;

      // generate the backgrounds depending
      // on limit and value.
      $track-content-height: 0.3em;
      background-image:
        var(--decrease-svg),
        var(--increase-svg),
        // color in the current value, split at the center of the thumb
        linear-gradient(
            to right,
            var(--bar-color) var(--thumb-center),
            var(--color-grey-dark) var(--thumb-center),
            var(--color-grey-dark) calc(var(--range-limit) * 1%),
            transparent calc(var(--range-limit) * 1%),
            transparent
          ),
        // background.
        linear-gradient(
            var(--color-background-close),
            var(--color-background-close)
          );
      background-repeat: no-repeat, no-repeat, no-repeat, no-repeat;
      background-size:
        calc(100% - 1em) 100%,
        calc(100% - 1em) 100%,
        100%,
        calc(100% - 10px) calc(100% - 2px);
      background-position: 0.5em, 0.5em, center, center;
    }

    @mixin input-range-thumb {
      background-color: var(--bar-color);
      height: 1.1em;
      width: 1.1em;
      border-radius: 50%;
      margin-top: -0.2em;
      box-shadow: 0 2px 5px var(--color-shadow-2);
      z-index: 1;
      border: none;
      cursor: ew-resize;
      appearance: none;
    }

    &::-webkit-slider-runnable-track {
      appearance: none;
      @include input-range-track;
    }
    &::-webkit-slider-thumb {
      appearance: none;
      @include input-range-thumb;
    }
    &:focus-visible {
      outline: none;
    }
    &:focus-visible::-webkit-slider-runnable-track {
      box-shadow: 0 0 0 2px black;
    }
    &:focus-visible::-moz-range-track {
      box-shadow: 0 0 0 2px black;
    }
    &:focus-visible::-webkit-slider-thumb {
      box-shadow: 0 0 0 3px black;
    }
    &:focus-visible::-moz-range-thumb {
      box-shadow: 0 0 0 3px black;
    }

    &::-moz-range-track {
      @include input-range-track;
    }
    &::-moz-range-thumb {
      @include input-range-thumb;
    }
  }

  &__tooltip {
    text-align: center;
    background-color: var(--color-background-close);
    padding: 0.5em;
    border-radius: 0.5em;
    box-shadow: 0 2px 5px var(--color-shadow-2);
    z-index: 1;
    white-space: nowrap;
    transition: opacity 100ms;
    pointer-events: none;
  }
  &__tooltip.transition-fade-leave-active {
    transition-delay: 100ms;
    transition-duration: 300ms;
  }

  &__oldvalue-marker {
    position: absolute;
    height: 1em;
    width: 1px;
    background-color: var(--color-text);
    z-index: 0;
  }
}
</style>
